import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { HttpClient } from '@angular/common/http';
import { FileOpener } from '@awesome-cordova-plugins/file-opener/ngx';
import { saveAs } from 'file-saver';
import { File } from '@awesome-cordova-plugins/file/ngx';
import { SharedServices } from '../shared/shared.service';
import { ContentObserver } from '@angular/cdk/observers';

@Injectable({
    providedIn: 'root',
})
export class FileService {
    constructor(private http: HttpClient, private file: File, private fileOpener: FileOpener, private sharedService: SharedServices) { }

    /** 📱 Devuelve la plataforma actual ('web', 'android' o 'ios') */
    getPlatform(): 'web' | 'android' | 'ios' {
        return Capacitor.getPlatform() as 'web' | 'android' | 'ios';
    }

    /** 🧾 Verifica permisos de almacenamiento en dispositivos nativos */
    async checkPermissions() {
        if (this.getPlatform() === 'web') return;

        try {
            const permResult: any = await Filesystem.checkPermissions();
  
            if (
                permResult.publicStorage !== 'granted' &&
                permResult.read !== 'granted' &&
                permResult.write !== 'granted'
            ) {
                const request: any = await Filesystem.requestPermissions();


                if (
                    request.publicStorage !== 'granted' &&
                    request.read !== 'granted' &&
                    request.write !== 'granted'
                ) {
                    throw new Error('Permisos de almacenamiento no concedidos');
                }
            }
        } catch (err) {
            console.error('⚠️ Error al verificar permisos:', err);
            throw new Error('No se pudieron obtener permisos de almacenamiento');
        }
    }

    /** ✏️ Guarda un archivo de texto simple (solo para pruebas) */
    async saveFile() {
        await this.checkPermissions();

        await Filesystem.writeFile({
            path: 'miArchivo.txt',
            data: 'Hola desde Capacitor!',
            directory: Directory.Documents,
            encoding: Encoding.UTF8,
        });

    }

    /** 📖 Lee un archivo previamente guardado */
    async readFile() {
        await this.checkPermissions();

        const result = await Filesystem.readFile({
            path: 'miArchivo.txt',
            directory: Directory.Documents,
            encoding: Encoding.UTF8,
        });

        return result.data;
    }
    async downloadFileSimple(url: string, fileName: string) {
        try {
            await this.checkPermissions();

            const blob: any = await this.http
                .get(url, { responseType: 'blob' })
                .toPromise();

            const base64Data = await this.convertBlobToBase64Simple(blob);

            await Filesystem.writeFile({
                path: fileName,
                data: base64Data as string,
                directory: Directory.Documents,
            });

 
            return `${Directory.Documents}/${fileName}`;
        } catch (error) {
            console.error('❌ Error al descargar archivo:', error);
            throw error;
        }
    }
    private async convertBlobToBase64Simple(blob: Blob): Promise<string | ArrayBuffer | null> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onerror = reject;
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(blob);
        });
    }
    /**
     * 🌐 Descarga y guarda un archivo desde una URL
     */
    async downloadFile(url: string, fileName: string) {

        const response = await fetch(url);
        if (!response.ok) throw new Error("No se pudo descargar el archivo");

        const type = response.headers.get("content-type") ?? "";


        const blob = await response.blob();

        // Detectar tipo REAL analizando las cabeceras
        const realExt = await this.detectRealExtension(blob, type);

        // Normalizar nombre final — evitar doble extensión
        let finalName = fileName;

        // Si el nombre NO termina con la extensión real → corregirlo
        if (!fileName.toLowerCase().endsWith(realExt)) {
            finalName = fileName.replace(/\.[^/.]+$/, ""); // Quitar la extensión vieja
            finalName += realExt; // Agregar la correcta
        }


        return await this.saveToAppFolder(blob, finalName);
    }

    async saveToAppFolder(blob: Blob, fileName: string) {
        try {
            fileName = fileName.replace(/[^\w.-]/g, "_");

          

            const base64 = await this.blobToBase64Safe(blob);

            await Filesystem.writeFile({
                path: fileName,
                data: base64,
                directory: Directory.Documents,
                recursive: true,
            });

            const fileUri = await Filesystem.getUri({
                path: fileName,
                directory: Directory.Documents,
            });

            const mime = this.getMimeType(fileName);

            // --------------------------------------------------------------
            // 🔥 ABRIR SIEMPRE SELECTOR DE APPS (SIN RECORDAR PREFERENCIA)
            // --------------------------------------------------------------
            await this.fileOpener.open(fileUri.uri, '*/*');

            return fileUri.uri;

        } catch (err) {
            console.error("❌ Error al guardar/abrir archivo:", err);
            return null;
        }
    }

    // Detecta MIME automáticamente
    private getMimeType(fileName: string): string {
        const ext = fileName.split('.').pop()?.toLowerCase() || '';

        const map: any = {
            pdf: 'application/pdf',
            jpg: 'image/jpeg',
            jpeg: 'image/jpeg',
            png: 'image/png',
            docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            doc: 'application/msword',
        };

        return map[ext] || 'application/octet-stream';
    }
    private async blobToBase64Safe(blob: Blob): Promise<string> {
        const chunkSize = 1024 * 1024; // 1 MB por chunk
        let base64 = "";

        const reader = blob.stream().getReader();
        const chunks: Uint8Array[] = [];

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            chunks.push(value);
        }

        // Unir todos los fragmentos en un único array
        let totalLength = chunks.reduce((acc, arr) => acc + arr.length, 0);
        let fullArray = new Uint8Array(totalLength);

        let offset = 0;
        for (let chunk of chunks) {
            fullArray.set(chunk, offset);
            offset += chunk.length;
        }

        // Convertir a base64 sin texto
        let binary = "";
        for (let i = 0; i < fullArray.length; i++) {
            binary += String.fromCharCode(fullArray[i]);
        }

        return btoa(binary);
    }

    private async detectRealExtension(blob: Blob, mime: string): Promise<string> {
        const buf = new Uint8Array(await blob.slice(0, 12).arrayBuffer());

        // PDF REAL empieza por %PDF
        if (buf[0] === 0x25 && buf[1] === 0x50 && buf[2] === 0x44) {
            return ".pdf";
        }

        // JPEG REAL comienza con FF D8
        if (buf[0] === 0xFF && buf[1] === 0xD8) {
            return ".jpg";
        }

        // PNG REAL comienza con 89 50 4E 47
        if (
            buf[0] === 0x89 && buf[1] === 0x50 &&
            buf[2] === 0x4E && buf[3] === 0x47
        ) {
            return ".png";
        }

        // DOCX / ZIP comienzan con PK (0x50 0x4B)
        if (buf[0] === 0x50 && buf[1] === 0x4B) {
            return ".docx";
        }

        // Si no se sabe → lo que dice el servidor
        if (mime.includes("pdf")) return ".pdf";
        if (mime.includes("jpg") || mime.includes("jpeg")) return ".jpg";
        if (mime.includes("png")) return ".png";

        return ".bin";
    }


    detectFromBlob(blob: Blob): string {
        const type = blob.type;
        if (type.includes("pdf")) return "pdf";
        if (type.includes("png")) return "png";
        if (type.includes("jpeg") || type.includes("jpg")) return "jpg";

        // Si viene vacío (Android WebView bug)
        return "bin";
    }

}
